import { html, type TemplateResult } from 'lit';
import { BRAND_UI } from '../../../shared/ui/infrastructure/brandAssets.ts';

export type LoginComponentTemplateProps = {
  appTitle: string;
  errorMessage: string;
  isLoading: boolean;
};
/** Returns the login component template. */
export function loginComponentTemplate(p: LoginComponentTemplateProps): TemplateResult {
  const logoSrc = BRAND_UI.alcaldiaLoginLogo;
  const backgroundSrc = BRAND_UI.loginBackground;
  return html`
    <style>
      .login-root {
        position: relative;
        isolation: isolate;
        display: flex;
        min-height: 100dvh;
        align-items: center;
        justify-content: center;
        padding: max(1.5rem, env(safe-area-inset-top)) 1rem max(1.5rem, env(safe-area-inset-bottom));
        background-color: var(--alcaldia-sky-900, #0d3d66);
        background-image: url('${backgroundSrc}');
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }
      .login-overlay {
        position: absolute;
        inset: 0;
        z-index: 0;
        pointer-events: none;
        /* Flat scrim for contrast on photography (no webby gradient). */
        background: color-mix(in srgb, var(--alcaldia-ink) 52%, transparent);
      }
      .login-card-wrap {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 28rem;
      }
    </style>
    <main class="login-root">
      <div class="login-overlay" aria-hidden="true"></div>
      <div class="login-card-wrap">
        <div
          class="rounded-[var(--md-shape-corner-extra-large)] p-8 shadow-[var(--md-elevation-3)]"
          style="background: var(--md-sys-color-surface-container-high); color: var(--md-sys-color-on-surface);"
        >
          <div class="mb-8 text-center">
            <img
              class="mx-auto mb-5 block max-h-44 w-auto max-w-full object-contain"
              src="${logoSrc}"
              width="320"
              height="160"
              alt="Alcaldía de Medellín — Distrito de Ciencia, Tecnología e Innovación"
            />
            <h1 class="text-xl font-normal tracking-tight">${p.appTitle}</h1>
            <p class="mt-2 text-sm" style="color: var(--md-sys-color-on-surface-variant);">Inicia sesión para continuar</p>
          </div>

          ${p.errorMessage
            ? html`
                <div
                  class="mb-6 flex items-start gap-3 rounded-md px-4 py-3 text-sm"
                  style="background: var(--md-sys-color-error-container); color: var(--md-sys-color-on-error-container);"
                >
                  <svg class="mt-0.5 h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                    />
                  </svg>
                  <p>${p.errorMessage}</p>
                </div>
              `
            : null}

          <form-component id="login-form"></form-component>

          ${p.isLoading
            ? html`
                <div class="mt-6 text-center">
                  <div class="inline-flex items-center gap-3" style="color: var(--md-sys-color-primary);">
                    <svg
                      class="h-5 w-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span class="text-sm font-medium">Entrando…</span>
                  </div>
                </div>
              `
            : null}
        </div>
      </div>
    </main>
  `;
}
